import { degreesToPercent, hideNode, percentToDegrees, showNode } from '../configurator/helpers';
import { EditMode } from '../configurator/model/edit-mode';
import { ImperialInputHandler } from '../utils/imperial-input';
import NumberFormatter, { UnitSystem } from '../utils/number-formatter';
import { DecimalSeparator } from '../utils/number-formatter';

export class HeightsUI {
  menus = [];

  constructor(options) {
    this.toolbar = document.querySelector(options.toolbarSelector);
    this.canvas = document.querySelector(options.canvasSelector);
    this.sidebar = document.querySelector(options.sidebarSelector);
    this.configurator = document.querySelector(options.configuratorSelector);
    this.heightPopup = document.querySelector(options.heightPopupSelector);
    this.slopePopup = document.querySelector(options.slopePopupSelector);
    this.overlay = document.querySelector(options.overlaySelector);
    this.help = document.querySelector(options.helpSelector);
    this.onZoomIn = options.onZoomIn;
    this.onZoomOut = options.onZoomOut;
    this.onSlopeToolSelected = options.onSlopeToolSelected;
    this.onAddPointsSelected = options.onAddPointsSelected;
    this.onEditPointsSelected = options.onEditPointsSelected;
    this.onMinHeightSelected = options.onMinHeightSelected;
    this.onHeightPopuConfirmed = options.onHeightPopupConfirmed;
    this.onHeightPopupRejected = options.onHeightPopupRejected;
    this.onSlopePopupConfirmed = options.onSlopePopupConfirmed;
    this.onSlopePopupRejected = options.onSlopePopupRejected;
    this.onModeChanged = options.onModeChanged;
    this.editMode = EditMode.POINTER;
    this.initialSlopeMode = options.initialSlopeMode;
    this.allButtons = [];
    this._initToolbar();
    this._initSidebar();
    this._initHeightPopup();
    this._initSlopePopup();
  }

  _initToolbar() {
    this.zoomInButton = this.toolbar.querySelector('[data-tool="zoom-in"]');
    this.zoomInButton.addEventListener('click', e => {
      e.preventDefault();
      this.onZoomIn();
    });

    this.zoomInButton = this.toolbar.querySelector('[data-tool="zoom-out"]');
    this.zoomInButton.addEventListener('click', e => {
      e.preventDefault();
      this.onZoomOut();
    });

    // keyDown listener for + and - zooming
    document.addEventListener('keydown', e => {
      switch (e.which) {
        case 187: // + (also allow =)
          e.stopPropagation();
          e.preventDefault();
          this.onZoomIn();
          break;
        case 107: // + on NumPad
          e.stopPropagation();
          e.preventDefault();
          this.onZoomIn();
          break;
        case 189: // - (also allow _)
          e.stopPropagation();
          e.preventDefault();
          this.onZoomOut();
          break;
        case 109: // - on NumPad
          e.stopPropagation();
          e.preventDefault();
          this.onZoomOut();
          break;
      }
    });

    this.modeButton = this.toolbar.querySelector('[data-tool="mode"]');
    this.modeButton.addEventListener('click', e => {
      e.preventDefault();
      if (this.editMode === EditMode.POINTER) {
        this.editMode = EditMode.MOVE;
        this.modeButton.querySelector('span').className = 'mptp-icon icon-export_selection-icon';
        dojo.style(this.canvas, 'cursor', 'move');
      } else {
        this.editMode = EditMode.POINTER;
        this.modeButton.querySelector('span').className = 'fa fa-arrows';
        dojo.style(this.canvas, 'cursor', 'default');
      }
      this.onModeChanged(this.editMode);
    });
  }

  _initSidebar() {
    const slopeButton = this.sidebar.querySelector('[data-tool="slope"]');
    if (slopeButton) {
      this.allButtons.push(slopeButton);
      slopeButton.addEventListener('click', e => {
        e.preventDefault();
        this._activateButton(slopeButton);
        this._updateHelp(HelpVersions.SLOPE);
        this.onSlopeToolSelected();
      });
    }

    const addPointsButton = this.sidebar.querySelector('[data-tool="add-points"]');
    if (addPointsButton) {
      this.allButtons.push(addPointsButton);
      addPointsButton.addEventListener('click', e => {
        e.preventDefault();
        this._activateButton(addPointsButton);
        this._updateHelp(HelpVersions.EDIT);
        this.addPointsSelected();
      });
    }

    const editPointsButton = this.sidebar.querySelector('[data-tool="edit-points"]');
    if (editPointsButton) {
      this.allButtons.push(editPointsButton);
      editPointsButton.addEventListener('click', e => {
        e.preventDefault();
        this._activateButton(editPointsButton);
        this._updateHelp(HelpVersions.GROUP);
        this.editPointsSelected();
      });
    }

    const resetLink = this.sidebar.querySelector('[data-tool="reset"]');
    if (resetLink) {
      this._initReset(resetLink);
    }
  }

  _initHeightPopup() {
    hideNode(this.heightPopup);
    const self = this;
    const popup = this.heightPopup;
    const input = self._getHeightInput();
    const confirmButton = this.heightPopup.querySelector('[data-tool="height-confirm"]');
    const rejectButton = this.heightPopup.querySelector('[data-tool="height-reject"]');

    const onConfirmed = () => {
      if (!this.isImperialHeightInput) {
        input.value = Number.parseInt(input.value) ? Number.parseInt(input.value) : self.minHeight;
      }
      if (input.value < self.minHeightLimit) {
        alert(tp_trans.trans('cannot_go_lower', 'cannot_go_lower', { height: self.minHeightLimit }));
        input.value = self.minHeight;
      } else {
        this.onHeightPopuConfirmed(input.value);
        hideNode(popup);
        this._hideOverlay();
      }
    };

    const onReject = () => {
      this.onHeightPopupRejected();
      hideNode(popup);
      this._hideOverlay();
    };

    input.addEventListener('keydown', e => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        onConfirmed();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onReject();
      } else if (e.key === 'ArrowUp') {
        input.value = Number.parseInt(input.value) + 1;
      } else if (e.key === 'ArrowDown') {
        input.value = Number.parseInt(input.value) - 1;
      }
    });

    confirmButton.addEventListener('click', e => {
      e.preventDefault();
      onConfirmed();
    });

    rejectButton.addEventListener('click', e => {
      e.preventDefault();
      onReject();
    });
  }

  _formatSlopeInput() {
    const input = this.slopePopup.querySelector('[data-tool="slope-input"]');
    input.value = this.slopeFormatter.formatDecimal(input.value);
  }

  _initSlopePopup() {
    hideNode(this.slopePopup);
    const popup = this.slopePopup;
    const input = this.slopePopup.querySelector('[data-tool="slope-input"]');
    const confirmButton = this.slopePopup.querySelector('[data-tool="slope-confirm"]');
    const rejectButton = this.slopePopup.querySelector('[data-tool="slope-reject"]');
    const modeSelect = this.slopePopup.querySelector('[data-tool="slope-mode"]');
    const decimalSeparator = input.classList.contains('decimal-dot') ? DecimalSeparator.DOT : DecimalSeparator.COMMA;
    this.slopeFormatter = new NumberFormatter({
      decimalSeparator,
      unitSystem: window.unitSystem === 'metric' ? UnitSystem.METRIC : UnitSystem.IMPERIAL,
    });

    modeSelect.value = this.initialSlopeMode;
    this._formatSlopeInput();

    const onConfirmed = () => {
      const value = this.slopeFormatter.formatFloat(input.value);
      if (this._validateSlope(value, modeSelect.value, input)) {
        this.onSlopePopupConfirmed(this._handoverDegrees(value, modeSelect.value), {
          value: value,
          mode: modeSelect.value,
        });
        hideNode(popup);
        this._hideOverlay();
      }
    };

    const onReject = () => {
      this.onSlopePopupRejected();
      hideNode(popup);
      this._hideOverlay();
    };

    input.addEventListener('keydown', e => {
      this._formatSlopeInput();
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        onConfirmed();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onReject();
      }
    });

    confirmButton.addEventListener('click', e => {
      e.preventDefault();
      onConfirmed();
    });

    rejectButton.addEventListener('click', e => {
      e.preventDefault();
      onReject();
    });

    modeSelect.addEventListener('change', e => {
      const modeType = e.target.value;
      const floatValue = this.slopeFormatter.formatFloat(input.value);
      switch (modeType) {
        case 'percent':
          input.value = parseFloat(degreesToPercent(floatValue).toFixed(2));
          break;
        case 'angle':
          input.value = parseFloat(percentToDegrees(floatValue).toFixed(2));
          break;
      }
      this._formatSlopeInput();
    });
  }

  _validateSlope(value, mode, input) {
    const maxPercent = 154;
    const maxAngle = 57;
    switch (mode) {
      case 'percent':
        if (value < 0) {
          alert(tp_trans.trans('slope_cannot_go_lower', 'slope_cannot_go_lower', { slope: '0%' }));
          input.value = 0;
          return false;
        } else if (value > maxPercent) {
          alert(tp_trans.trans('slope_cannot_go_higher', 'slope_cannot_go_higher', { slope: `${maxPercent}%` }));
          input.value = maxPercent;
          return false;
        }
        break;
      case 'angle':
        if (value < 0) {
          alert(tp_trans.trans('slope_cannot_go_lower', 'slope_cannot_go_lower', { slope: '0°' }));
          input.value = 0;
          return false;
        } else if (value > maxAngle) {
          alert(tp_trans.trans('slope_cannot_go_higher', 'slope_cannot_go_higher', { slope: `${50}°` }));
          input.value = maxAngle;
          return false;
        }
        break;
    }

    return true;
  }

  _handoverDegrees(value, mode) {
    switch (mode) {
      case 'percent':
        return percentToDegrees(value);
      case 'angle':
        return value;
    }
  }

  _acceptDegrees(value, mode) {
    switch (mode) {
      case 'percent':
        return parseFloat(degreesToPercent(value).toFixed(2));
      case 'angle':
        return value;
    }
  }

  onSlopeToolSelected() {
    this.onSlopeToolSelected();
  }

  addPointsSelected() {
    this.onAddPointsSelected();
  }

  editPointsSelected() {
    this.onEditPointsSelected();
  }

  minHeightButtonSelected() {
    this.onMinHeightSelected();
  }

  _activateButton(button) {
    this._deactivateAllButtons();
    button.classList.add('active');
  }

  _deactivateAllButtons() {
    for (let button of this.allButtons) {
      button.classList.remove('active');
    }
  }

  openHeightPopup(point, options) {
    const input = this._getHeightInput();
    input.value = options.defaultValue ?? point?.height;
    this.minHeight = options.minHeight;
    this.minHeightLimit = options.minHeightLimit;
    this._positionAndShowPopup(this.heightPopup, options.coordinates);
    input.focus();
    input.select();
    this._showOverlay();
  }

  openSlopePopup(point, options) {
    const input = this.slopePopup.querySelector('[data-tool="slope-input"]');
    const mode = this.slopePopup.querySelector('[data-tool="slope-mode"]');
    input.value = this._acceptDegrees(options.defaultValue, mode.value);
    this._formatSlopeInput();
    this._positionAndShowPopup(this.slopePopup, options.coordinates);
    input.focus();
    input.select();
    this._showOverlay();
  }

  _positionAndShowPopup(popup, coordinates) {
    popup.style.position = 'absolute';
    popup.style.left = `${coordinates.x}px`;
    popup.style.top = `${coordinates.y}px`;
    showNode(popup, 'flex');
  }

  _showOverlay() {
    showNode(this.overlay);
  }

  _hideOverlay() {
    hideNode(this.overlay);
  }

  selectAddTool() {
    const addPointsButton = this.sidebar.querySelector('[data-tool="add-points"]');
    this._activateButton(addPointsButton);
    this._updateHelp(HelpVersions.EDIT);
    this.addPointsSelected();
  }

  selectSlopeTool() {
    const slopeToolButton = this.sidebar.querySelector('[data-tool="slope"]');
    this._activateButton(slopeToolButton);
    this._updateHelp(HelpVersions.SLOPE);
    this.onSlopeToolSelected();
  }

  updateHeightRange(range) {
    const numberFormatter = new NumberFormatter({});
    const heightRangeTool = this.sidebar.querySelector('[data-tool="height-range"]');
    if (!heightRangeTool) {
      return;
    }
    const rangeText = numberFormatter.formatRangeOutput(range.min, range.max, 'feet_inch_fraction');
    heightRangeTool.innerHTML = rangeText;
  }

  _updateHelp(tool) {
    const outletHeader = this.help.querySelector('[data-tool-help="header"]');
    const outletContent = this.help.querySelector('[data-tool-help="content"');
    switch (tool) {
      case HelpVersions.EDIT:
        outletHeader.innerHTML = tp_trans.trans('height_conf_add_button', 'height_conf_add_button');
        outletContent.innerHTML = tp_trans.trans('help_add', 'help_add');
        break;
      case HelpVersions.SLOPE:
        outletHeader.innerHTML = tp_trans.trans('height_conf_slope_button', 'height_conf_slope_button');
        outletContent.innerHTML = tp_trans.trans('help_slope', 'help_slope');
        break;
      case HelpVersions.GROUP:
        outletHeader.innerHTML = tp_trans.trans('height_conf_edit_button', 'height_conf_edit_button');
        outletContent.innerHTML = tp_trans.trans('help_group_edit', 'help_group_edit');
        break;
    }
  }

  _initReset(reset) {
    reset.addEventListener('click', e => {
      e.preventDefault();
      if (window.confirm(tp_trans.trans('reset_height_tool', 'reset_height_tool'))) {
        document.querySelector('input[name="action"]').value = 'reset_height_selection';
        document.forms['form_height_selection_tool'].submit();
      }
    });
  }

  _getHeightInput() {
    const input = this.heightPopup.querySelector('[data-tool="height-input"]');
    if (input.tagName === 'INPUT') {
      this.isImperialHeightInput = false;
      return input;
    } else {
      this.isImperialHeightInput = true;
      return new ImperialInputHandler(input);
    }
  }
}

const HelpVersions = Object.freeze({
  EDIT: Symbol('edit'),
  GROUP: Symbol('group'),
  SLOPE: Symbol('slope'),
});
